import React from 'react';
import { css } from '@emotion/core';
import PropTypes from 'prop-types';
import { Accordion, AccordionItem, Button } from 'carbon-components-react';
import { ArrowRight16, ArrowLeft16 } from '@carbon/icons-react';
import { respond } from '../../theme/mixins';
import { NavItem, Text, ProgressBar, Pager } from '..';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import times from 'lodash/times';
import { FormattedMessage } from 'react-intl';

const Navigation = ({
  end,
  currentStep,
  currentStepIsLastStep,
  currentScenario,
  steps,
  advance,
  rewind,
  triggerRestart,
  hasOneScenario
}) => {
  const intlBase = `${currentScenario.key}.${currentStep.key}`;
  const { withBody, withMore, withBullets, withNextSteps } = currentStep;

  function makeBulletConfig(bullet) {
    if (isString(bullet)) return { key: bullet };
    return bullet;
  }

  function makeBulletsConfig(bullets) {
    if (isNumber(bullets)) return times(bullets, (i) => makeBulletConfig(`bullet${i + 1}`));
    return bullets.map((bullet) => makeBulletConfig(bullet));
  }

  const bullets = withBullets ? makeBulletsConfig(withBullets) : null;

  const { ctaUrl } = end;

  return (
    <NavItem>
      <div css={styles.layout}>
        <Text as="h4" className="interactive-widget__step-title" intl={`${intlBase}.title`} />
        <ProgressBar currentStep={currentStep} steps={steps} />
        <div css={styles.body}>
          {withBody && <Text className="interactive-widget__body" intl={`${intlBase}.body`} />}
          {withBullets && (
            <>
              <ul css={styles.bulletList}>
                {bullets.map((bullet) => (
                  <Text
                    key={bullet.key}
                    as="li"
                    className="interactive-widget__body"
                    intl={`${intlBase}.${bullet.key}`}
                  />
                ))}
              </ul>
            </>
          )}
          <div css={styles.actions}>
            {currentStepIsLastStep && (
              <div>
                {ctaUrl ? (
                  <Button href={ctaUrl} renderIcon={ArrowRight16} css={styles.button}>
                    <FormattedMessage id="template.end.cta" defaultMessage="Talk to an expert" />
                  </Button>

                ) : (
                  <Button data-ibm-contact="scheduler-link" renderIcon={ArrowRight16} css={styles.button}>
                    <FormattedMessage id="template.end.cta" defaultMessage="Talk to an expert" />
                  </Button>
                )}
              </div>
            )}
            {withNextSteps && (
              <div>
                <Text css={styles.nextStepsHeader} intl={`template.nextSteps`}>
                  Next Steps
                </Text>
                <Text className="interactive-widget__body" intl={`${intlBase}.nextSteps`} />
              </div>
            )}
          </div>
          {withMore && (
            <Accordion css={styles.accordion}>
              <AccordionItem title={<Text intl="more.Details">More details</Text>}>
                <Text intl={`${intlBase}.moreDetails`} />
              </AccordionItem>
            </Accordion>
          )}
        </div>
        <div css={styles.footer}>
          {hasOneScenario ?
            <span css={styles.backButton} />
           : (
            <button onClick={triggerRestart} css={styles.backButton}>
              <ArrowLeft16 css={styles.backButtonIcon} />
              <Text intl={`template.menu`} />
            </button>
          )}
          <Pager advance={advance} rewind={rewind} />
        </div>
      </div>
    </NavItem>
  );
};

const styles = {
  bulletList: css`
    margin: 16px;
    list-style: disc;
  `,
  bullet: css`
    border: 1px solid blue;
  `,
  layout: css`
    min-block-size: 35.38rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    ${respond(`
      min-block-size: auto;
    `)}
  `,
  body: css`
    flex-grow: 1;
  `,
  actions: css`
    margin-block-start: 2rem;
  `,
  footer: css`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
  `,
  nextStepsHeader: css`
    font-size: 12px;
    color: var(--color-text-secondary, #565656);
    margin-bottom: 8px;
  `,
  accordion: css`
    margin-top: 2rem;
    border-bottom: 0;
    .bx--accordion__item {
      margin-left: -14px;
      margin-right: -14px;
      line-height: 18px;
      border-bottom: 0;
    }
    .bx--accordion__content {
      padding-right: 3em;
      padding-bottom: 0;
      margin-bottom: 1em;
      q {
        quotes: "'" "'" !important;
        &:before {
          content: '“';
        }
        &:after {
          content: '”';
        }
      }
      address {
        margin-top: 0.5em;
      }
    }
  `,
  startEndButton: css`
    margin-top: 1rem;
  `,
  backButton: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0 0 0.125rem;
    color: var(--color-text-secondary);
    background-color: transparent;
  `,
  backButtonIcon: css`
    inline-size: 1.5625rem;
    block-size: 1.25rem;
  `,
};

Navigation.propTypes = {
  end: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  hasOneScenario: PropTypes.bool.isRequired,
  currentStep: PropTypes.func.isRequired,
  currentStepIsLastStep: PropTypes.bool.isRequired,
  steps: PropTypes.array.isRequired,
  advance: PropTypes.func.isRequired,
  rewind: PropTypes.func.isRequired,
  triggerRestart: PropTypes.func.isRequired,
  triggerEnd: PropTypes.func.isRequired,
};

export default Navigation;
